import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { affiliateService } from "../../services/affiliate.service";
import { format } from "date-fns";
import { toast } from "react-hot-toast";
import { Mail } from "react-feather";
import { EmptyState } from "@/components/ui/empty-state";

interface BrandDetail {
  id: string;
  companyName: string;
  brandProfileImage: string;
  website: string;
  description: string;
}

interface Program {
  id: string;
  commissionType: "PERCENTAGE";
  commissionValue: string;
  discountValue: string;
  minimumPayoutAmount: string;
  automaticApproval: boolean;
  status: "ACTIVE";
}

interface Invitation {
  id: string;
  email: string;
  status: "PENDING" | "ACCEPTED" | "REJECTED";
  expiresAt: string;
  program: Program;
  brandId: string;
  brandDetail: BrandDetail;
}

export function Invitations() {
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchInvitations = async () => {
      try {
        const data = await affiliateService.getAffiliateInvitations();
        setInvitations(data);
      } catch (error) {
        console.error("Failed to fetch invitations:", error);
        toast.error("Failed to load invitations");
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvitations();
  }, []);

  const handleInvitationClick = (brandId: string) => {
    navigate(`/affiliate/brands/${brandId}`);
  };

  if (isLoading) {
    return (
      <div className="animate-pulse">
        <h1 className="text-2xl font-semibold tracking-tight text-foreground mb-8">
          Program Invitations
        </h1>
        {[...Array(3)].map((_, index) => (
          <div key={index} className="mb-4 bg-card p-6 rounded-lg shadow-sm">
            <div className="flex items-center gap-4 mb-4">
              <div className="w-12 h-12 bg-muted rounded-full" />
              <div>
                <div className="h-5 w-48 bg-muted rounded mb-2" />
                <div className="h-4 w-32 bg-muted rounded" />
              </div>
            </div>
            <div className="h-4 w-3/4 bg-muted rounded mb-3" />
            <div className="h-4 w-1/2 bg-muted rounded" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight text-foreground mb-8">
        Program Invitations
      </h1>

      {invitations.length === 0 ? (
        <EmptyState
          icon={<Mail />}
          title="No invitations yet"
          body="Invitations from brands will show up here"
        />
      ) : (
        <div className="space-y-4">
          {invitations.map((invitation) => (
            <div
              key={invitation.id}
              onClick={() => handleInvitationClick(invitation.brandId)}
              className="bg-card rounded-lg shadow-sm border border-border hover:border-brand-500 cursor-pointer transition-all duration-200"
            >
              <div className="p-6">
                {/* Header with Brand Info */}
                <div className="flex items-center gap-4 mb-4">
                  <img
                    src={invitation.brandDetail.brandProfileImage}
                    alt={invitation.brandDetail.companyName}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">
                      {invitation.brandDetail.companyName} invited you to join
                      their program
                    </h3>
                    <a
                      href={invitation.brandDetail.website}
                      onClick={(e) => e.stopPropagation()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-muted-foreground hover:text-brand-600"
                    >
                      {invitation.brandDetail.website.replace(
                        /^https?:\/\//,
                        ""
                      )}
                    </a>
                  </div>
                </div>

                {/* Program Details */}
                <div className="space-y-2 mb-4 text-sm">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Commission:</span>
                    <span className="font-medium">
                      {invitation.program.commissionValue}%
                    </span>
                    {invitation.program.discountValue && (
                      <>
                        <span className="text-muted-foreground ml-4">
                          Customer Discount:
                        </span>
                        <span className="font-medium">
                          {invitation.program.discountValue}%
                        </span>
                      </>
                    )}
                  </div>
                  {invitation.program.automaticApproval && (
                    <div className="text-green-600 text-sm">
                      Automatic Approval
                    </div>
                  )}
                </div>

                {/* Status and Dates */}
                <div className="flex justify-between items-center text-sm border-t pt-4">
                  <div className="flex items-center gap-2">
                    <span className="text-muted-foreground">Status:</span>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        invitation.status === "PENDING"
                          ? "bg-yellow-100 text-yellow-800"
                          : invitation.status === "ACCEPTED"
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {invitation.status}
                    </span>
                  </div>
                  <div className="flex gap-4 text-muted-foreground">
                    <div>
                      {invitation.status === "PENDING" && (
                        <span className="text-green-600">
                          Expires:{" "}
                          {format(
                            new Date(invitation.expiresAt),
                            "MMM d, yyyy"
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
